import { PLATFORM_COLORS, getTikTokColor } from "./chartUtils";

import { useMemo } from "react";

// Types for analytics data structure
export interface HistoricalDataEntry {
  date: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  videosWithData: number;
}

export interface PlatformData {
  platform: string;
  historicalData: HistoricalDataEntry[];
}

export interface AnalyticsData {
  platforms?: PlatformData[];
}

export interface ChartData {
  viewsChart: Array<Record<string, string | number>>;
  videosChart: Array<Record<string, string | number>>;
  pieChart: Array<{
    platform: string;
    views: number;
    percentage: number;
    color: string;
  }>;
}

export const useChartData = (
  analyticsData: AnalyticsData | undefined,
): ChartData => {
  return useMemo(() => {
    if (!analyticsData?.platforms?.length)
      return { viewsChart: [], videosChart: [], pieChart: [] };

    // Get all unique dates across all platforms
    const allDates = new Set<string>();
    analyticsData.platforms.forEach((platform) => {
      platform.historicalData.forEach((entry: HistoricalDataEntry) => {
        allDates.add(entry.date);
      });
    });

    const sortedDates = Array.from(allDates).sort();

    // Create views chart data
    const viewsChart = sortedDates.map((date) => {
      const dataPoint: Record<string, string | number> = { date };

      analyticsData.platforms?.forEach((platform) => {
        const entry = platform.historicalData.find(
          (h: HistoricalDataEntry) => h.date === date,
        );
        dataPoint[platform.platform] = entry ? entry.views : 0;
      });

      return dataPoint;
    });

    // Create videos posted chart data
    const videosChart = sortedDates.map((date) => {
      const dataPoint: Record<string, string | number> = { date };

      analyticsData.platforms?.forEach((platform) => {
        const entry = platform.historicalData.find(
          (h: HistoricalDataEntry) => h.date === date,
        );
        dataPoint[platform.platform] = entry ? entry.videosWithData : 0;
      });

      return dataPoint;
    });

    // Create pie chart data - use latest historical data
    const pieChartData: Array<{
      platform: string;
      views: number;
      percentage: number;
      color: string;
    }> = [];

    let totalHistoricalViews = 0;

    // Calculate total views from latest historical data for each platform
    analyticsData.platforms.forEach((platform) => {
      const latestData =
        platform.historicalData[platform.historicalData.length - 1];
      const views = latestData ? latestData.views : 0;
      totalHistoricalViews += views;

      if (views > 0) {
        pieChartData.push({
          platform: platform.platform,
          views: views,
          percentage: 0, // Will calculate after we have total
          color:
            platform.platform === "tiktok"
              ? getTikTokColor()
              : PLATFORM_COLORS[
                  platform.platform as keyof typeof PLATFORM_COLORS
                ] || PLATFORM_COLORS.unknown,
        });
      }
    });

    // Calculate percentages
    const pieChart = pieChartData.map((item) => ({
      ...item,
      percentage:
        totalHistoricalViews > 0
          ? Math.round((item.views / totalHistoricalViews) * 100)
          : 0,
    }));

    return { viewsChart, videosChart, pieChart };
  }, [analyticsData]);
};
